import { calculate1RM, estimate1RM, SetStats } from './set-stats';

/**
 * What one stored set says about itself: do the figures the app calculated still match the weight
 * and reps the set records?
 *
 * Why this exists (#5175). A set stores what the user recorded — `weight_kg`, `reps` and `bw_pc`,
 * how much of the load the user's own body carries — plus three figures `estimate1RM` calculates
 * from them. #4556 found sets where the recorded weight had been rewritten afterwards and the
 * calculated figures left behind, and the app went on prescribing weights built from the figures.
 * The write paths inside the app were closed by bf49fb31e; this tells a reader whether a set
 * already on disk carries the damage.
 */
export enum SetStatsConsistency {
  /** the calculated figures agree with what the set records */
  Consistent = 'Consistent',
  /**
   * The exercise adds no bodyweight, so `weight_net_kg` must be the recorded weight itself — and
   * it is not. This is the exact shape #4556 reported, and it needs no tolerance: with no
   * bodyweight share `estimate1RM` writes the recorded weight through unchanged.
   */
  ExactMismatch = 'ExactMismatch',
  /**
   * The three calculated figures contradict each other, whatever the user weighed on the day.
   * The user's bodyweight at the time of the set is stored nowhere, so `weight_net_kg` itself
   * cannot be rebuilt — but it cancels out of the two relations checked below, so a set that was
   * edited afterwards is still recognisable. Repairing such a set is a separate question.
   */
  DerivedInconsistent = 'DerivedInconsistent',
  /** the set carries no calculated figures, or is missing something the checks need */
  NotCheckable = 'NotCheckable',
}

/**
 * How far two figures may sit apart and still count as the same number.
 *
 * Relative, not absolute, because these are lb-to-kg conversions with long tails (35 lb is stored
 * as 15.875732950000002). Recomputing the same formula should land on the same double, but a
 * figure written by an older app version, or read back through JSON, may differ in the last bit —
 * and that is float noise, not a rewritten weight. A real edit moves the figures by kilograms.
 */
const RELATIVE_TOLERANCE = 1e-9;

function isSameNumber(a: number, b: number): boolean {
  return Math.abs(a - b) <= RELATIVE_TOLERANCE * Math.max(1, Math.abs(a), Math.abs(b));
}

function isUsableNumber(value: number | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** Judge one stored set. Reads only; never changes what it is given. */
export function classifySetStats(stats: SetStats | undefined | null): SetStatsConsistency {
  if (!stats) {
    return SetStatsConsistency.NotCheckable;
  }

  const { weight_kg, reps, weight_net_kg, estimated1rm_net_kg, estimated1rm_kg } = stats;

  // without the recorded values there is nothing to check the calculated ones against
  if (!isUsableNumber(weight_kg) || !isUsableNumber(reps) || Math.round(reps) <= 0) {
    return SetStatsConsistency.NotCheckable;
  }

  // `estimate1RM` deletes all three figures together when it cannot calculate them, so a set
  // missing any of them was never given a full set of figures to disagree with
  if (!isUsableNumber(weight_net_kg) || !isUsableNumber(estimated1rm_net_kg) || !isUsableNumber(estimated1rm_kg)) {
    return SetStatsConsistency.NotCheckable;
  }

  const bw_pc = stats.bw_pc || 0;

  // No bodyweight share: `estimate1RM` writes the recorded weight straight into weight_net_kg, so
  // anything else is the reported corruption and exact equality is the right test.
  //
  // Exactly zero, not "zero or less". `estimate1RM` applies whatever share it is given, so a
  // negative one legitimately stores a net weight BELOW the recorded weight — the one input for
  // which "this needs no tolerance" would be wrong. Nothing was found that writes a negative
  // share, so this is very likely unreachable; it costs one character to not depend on that.
  // Such a set still goes through the two relations below, which do not care about the share.
  if (bw_pc === 0 && weight_net_kg !== weight_kg) {
    return SetStatsConsistency.ExactMismatch;
  }

  // the user's bodyweight on the day is stored nowhere, so it cannot be checked directly. It
  // cancels out of these two relations, which therefore hold for every honest set:
  //
  //   1. the one-rep max is what the NET load and the reps produce, with no bodyweight involved
  //   2. the added-weight one-rep max sits exactly one bodyweight share below the net one, and
  //      that share is the difference between the net and the recorded weight
  //
  // rewriting the reps afterwards breaks the first; rewriting the weight breaks the second
  const expected1rmNet_kg = calculate1RM(weight_net_kg, reps, 0, 0).estimated1rm_net_kg;
  if (!isSameNumber(estimated1rm_net_kg, expected1rmNet_kg)) {
    return SetStatsConsistency.DerivedInconsistent;
  }

  const expected1rmAdded_kg = estimated1rm_net_kg - (weight_net_kg - weight_kg);
  if (!isSameNumber(estimated1rm_kg, expected1rmAdded_kg)) {
    return SetStatsConsistency.DerivedInconsistent;
  }

  return SetStatsConsistency.Consistent;
}

/** One set `repairStaleSetFigures` recalculated, with the one-rep max it held before and after. */
export interface RepairedSetFigures {
  index: number;
  before1rm_net_kg: number;
  after1rm_net_kg: number;
}

/**
 * Recalculate the three figures of every set whose figures cannot be right, and say which ones.
 *
 * Why (#5175). bf49fb31e stopped the app writing such sets, but the ones already on disk stay:
 * the production sweep found 789 of them in 380 accounts. They no longer decide a prescribed
 * weight (#5176 caps that from the recorded weight and reps), but the progress chart, the CSV
 * export and the "previous session" figure still read them — the #4556 user's chart shows a
 * 103.7 lb one-rep max for a 35 lb x 16 set.
 *
 * Only an `ExactMismatch` set is repaired. It has no bodyweight share, so `estimate1RM` does not
 * use the bodyweight at all and recalculating gives exactly what the app would have stored. A
 * `DerivedInconsistent` set is left as it is: its figures depend on what the user weighed on the
 * day, which is stored nowhere, so any recalculation would be a guess.
 *
 * Mutates in place, like `healSparseLogSets`. A repaired set classifies as consistent afterwards,
 * so calling this again on the same sets repairs and reports nothing.
 */
export function repairStaleSetFigures(sets: ReadonlyArray<{ stats?: SetStats } | null | undefined> | undefined): RepairedSetFigures[] {
  const repaired: RepairedSetFigures[] = [];
  (sets ?? []).forEach((set, index) => {
    // if the set's figures are exactly wrong, recalculate them from what it records
    if (set?.stats && classifySetStats(set.stats) === SetStatsConsistency.ExactMismatch) {
      const before1rm_net_kg = set.stats.estimated1rm_net_kg;
      // no bodyweight share, so the bodyweight passed here does not enter any of the three figures
      estimate1RM(set.stats, 0);
      repaired.push({ index, before1rm_net_kg, after1rm_net_kg: set.stats.estimated1rm_net_kg });
    }
  });
  return repaired;
}
